import React from 'react';

import { Box, Paper, Typography } from '@mui/material';

import { lightPalette, borders, palette } from '@app/theme';

import type { FeatureCardProperties } from './FeatureCard.types';

export const FeatureCard: React.FC<FeatureCardProperties> = ({
  feature,
  dataTest,
}) => {
  return (
    <Paper
      elevation={0}
      data-testid={dataTest}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 0,
        backgroundColor: 'background.paper',
        borderRight: borders.strong,
        borderBottom: borders.strong,
        boxShadow: 'none',
        transition: 'background 0.15s',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          background: lightPalette.background.default,
        },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: { xs: 2, md: 2.5 },
          color: lightPalette.text.primary,
          fontSize: '1.5rem',
        }}
        data-testid={`${dataTest}_icon`}
      >
        {feature.icon}
      </Box>

      <Typography
        variant='h6'
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          color: 'text.primary',
          mb: { xs: 1, md: 1.5 },
          textTransform: 'uppercase',
          letterSpacing: '-0.02em',
        }}
        data-testid={`${dataTest}_title`}
      >
        {feature.title}
      </Typography>

      <Typography
        variant='body2'
        sx={{
          fontSize: { xs: '0.875rem', md: '0.9375rem' },
          color: 'text.secondary',
          lineHeight: 1.6,
          mb: feature.bullets || feature.tags || feature.example ? 1.5 : 0,
        }}
        data-testid={`${dataTest}_description`}
      >
        {feature.description}
      </Typography>

      {feature.bullets && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          data-testid={`${dataTest}_bullets`}
        >
          {feature.bullets.map((bullet) => (
            <Box
              key={bullet.text}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: bullet.dotColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant='caption'
                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
              >
                {bullet.text}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {feature.tags && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
          data-testid={`${dataTest}_tags`}
        >
          {feature.tags.map((tag) => (
            <Box
              key={tag.label}
              sx={{
                px: 1.25,
                py: 0.375,
                backgroundColor: 'transparent',
                color: lightPalette.text.primary,
                fontSize: '0.625rem',
                fontWeight: 600,
                borderRadius: 0,
                border: borders.strong,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {tag.label}
            </Box>
          ))}
        </Box>
      )}

      {feature.example && (
        <Box
          sx={{
            backgroundColor: lightPalette.background.default,
            borderRadius: 0,
            p: 1.5,
            borderLeft: `3px solid ${palette.primary.main}`,
          }}
          data-testid={`${dataTest}_example`}
        >
          <Typography
            sx={{
              color: feature.example.accentColor,
              fontSize: '0.625rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 0.5,
            }}
          >
            {feature.example.title}
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.5,
              fontStyle: 'italic',
            }}
          >
            {feature.example.text}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
